import {
  Box,
  Button,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';
import { Row } from './Row';
import { useState } from 'react';
import { Add } from '@mui/icons-material';
import useTable from 'src/hooks/useTable';
import { ConditionForm } from './ConditionForm';
import { TableHeadCustom } from 'src/components/table';
import { useConditions } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { TABLE_HEAD_CONDITION } from 'src/careflow/common/table-head';
import { v4 as uuidv4 } from 'uuid';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { translateTableHead } from 'src/careflow/helpers/common';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { Property } from 'csstype';

type Props = {
  patient: WrappedPatient | null;
  handler?: Function;
  noWrap?: boolean;
  position?: Property.Position;
};

export function ConditionsList({ patient, handler, noWrap = true, position = 'absolute' }: Props) {
  const [add, setOpenAdd] = useState(false);
  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({});

  const { i18n } = useLocales();

  const [conditions] = useConditions({
    filter: { patient: patient?.id },
    autofetch: !!patient?.id,
  });

  const paginatedConditions =
    conditions?.slice?.(page * rowsPerPage, page * rowsPerPage + rowsPerPage) ?? [];

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <TableContainer>
            <Stack sx={{ alignContent: 'end', flexWrap: 'wrap', m: 1 }}>
              {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.CONDITION.ADD] }) && (
                <Button
                  size="small"
                  variant="contained"
                  sx={{ height: '36px' }}
                  startIcon={<Add />}
                  style={{ position: position, top: '25px', right: '25px' }}
                  onClick={() => setOpenAdd(true)}
                >
                  {i18n('patients.details.conditions.button', 'crs')}
                </Button>
              )}
            </Stack>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHeadCustom headLabel={translateTableHead(TABLE_HEAD_CONDITION, i18n, 'crs')} />
              <TableBody>
                {paginatedConditions.length ? (
                  paginatedConditions.map((row: any) => (
                    <Row
                      key={uuidv4()}
                      row={row}
                      patient={patient}
                      handler={handler}
                      noWrap={noWrap}
                    />
                  ))
                ) : (
                  <TableCell colSpan={TABLE_HEAD_CONDITION?.length}>
                    <Typography variant="body2" align="center">
                      No rows
                    </Typography>
                  </TableCell>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={conditions?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      </Box>
      <ConditionForm
        patient={patient}
        appointment={null}
        open={add}
        onClose={() => setOpenAdd(false)}
        handler={handler}
      />
    </>
  );
}
